import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import * as d3 from 'd3'
import { useAuth0 } from '@auth0/auth0-react'
import { useSearchParams } from 'react-router-dom'
import pick from 'lodash/pick'

import { AppLayoutContext } from '../../../providers/AppLayout'
import { useUrlStateParams } from '../../../utils/url'
import { APP_UPDATE_NOTICE, SnackbarContext } from '../../../providers/SnackbarContext'

import { AppPermissions } from '../../../models/AppPermissions.model'
import { Trail, trailFields } from '../../../models/Trail.model'
import { TrailRelations } from '../../../models/TrailRelations.model'
import { TrailSystem } from '../../../models/TrailSystem.model'

import UserService from '../../../services/UserService/UserService'
import { TrailService } from '../../../services/TrailService/TrailService'
import { TrailSystemService } from '../../../services/TrailSystemService/TrailSystemService'

import {
  Box,
  FormControl,
  OutlinedInput,
  InputLabel,
  MenuItem,
  Stack,
  Typography,
  Button,
  InputAdornment,
  IconButton,
  Paper,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Chip,
} from '@mui/material'
import { GridColDef } from '@mui/x-data-grid'
import CloseIcon from '@mui/icons-material/Close'
import AddIcon from '@mui/icons-material/Add'
import ImportExportIcon from '@mui/icons-material/ImportExport'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import DataTable from '../../../components/DataTable/DataTable.component'
import { ID_COLUMN_WIDTH, NAME_COLUMN_MAX_WIDTH } from '../../../components/DataTable/DataTable.constants'
import EllipsisCell from '../../../components/DataTable/EllipsisCell.component'
import Loading from '../../../components/Loading/Loading.component'
import ImportExportDialog, {
  ImportExportDialogProps,
} from '../../../components/ImportExportDialog/ImportExportDialog.component'
import Link from '@mui/material/Link'
import Breadcrumbs from '../../../components/Breadcrumbs/Breadcrumbs.component'
import { TrailSearchEvent } from '../../../events/TrailSearchEvent'
import TrailForm from '../shared/TrailForm.component'
import FormDialog from '../../../components/FormDialog/FormDialog.component'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { AppStateContext } from '../../../providers/AppStateContext'

type TrailSearchProps = {
  nested?: boolean
  systemId?: number
  hideHeader?: boolean
  hideBreadcrumbs?: boolean
  hideActions?: boolean
  disableImport?: boolean
  disablePadding?: boolean
  excludeColumns?: string[]
  defaultParams?: TrailSearchEvent
  extraRowActions?: {
    label: string
    onClick: (row?: Trail) => void
  }[]
}
const TrailSearch = forwardRef((props: TrailSearchProps, ref): JSX.Element => {
  const [currentSearchParameters] = useSearchParams()
  const {
    nested,
    systemId = Number(currentSearchParameters.get('trail-system')) || undefined,
    hideHeader = false,
    hideBreadcrumbs = false,
    hideActions = false,
    disableImport: requestedDisableImport = false,
    disablePadding = false,
    excludeColumns = [],
    defaultParams = {},
    extraRowActions = [],
  } = props

  const [isSaving, setIsSaving] = useState(false)

  const { user } = useAuth0()
  const hasWritePermissions = UserService.hasPermissions(user, [AppPermissions.editTrail])
  const hasWeatherGroupPermissions = UserService.hasPermissions(user, [AppPermissions.viewWeatherGroup])
  const hasConditionalStatusPermissions = UserService.hasPermissions(user, [AppPermissions.viewConditionalStatus])

  const [searchError, setSearchError] = useState('')
  const [rowActionsAnchorEl, setRowActionsAnchorEl] = useState<null | HTMLElement>(null)
  const isActionsMenuOpen = Boolean(rowActionsAnchorEl)
  const [selectedRow, setSelectedRow] = useState<Trail | undefined>()
  const [isAdding, setIsAdding] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false)

  const [name, setName] = useUrlStateParams(
    '',
    'name',
    (value) => encodeURIComponent(value.toString()),
    (value) => (value ? decodeURIComponent(value) : ''),
  )
  const nameInputRef = useRef<HTMLInputElement>()
  const [orderBy, setOrderBy] = useState<keyof Trail | undefined>('name')
  const [orderDirection, setOrderDirection] = useState<'asc' | 'desc' | undefined>('asc')
  const [page, setPage] = useUrlStateParams(
    1,
    'page',
    (value) => value.toString(),
    (value) => (!isNaN(Number(value)) ? Number(value) : 1),
  )
  const [pageSize, setPageSize] = useUrlStateParams(
    96,
    'page-size',
    (value) => value.toString(),
    (value) => (!isNaN(Number(value)) ? Number(value) : 12),
  )

  const [trailSystem, setTrailSystem] = useState<TrailSystem>()

  const [isImportExportOpen, setIsImportExportOpen] = useState(false)

  const disableImport = useMemo(() => {
    return !hasWritePermissions || requestedDisableImport
  }, [hasWritePermissions, requestedDisableImport])

  const trailService = useMemo(() => new TrailService(), []) // memo to prevent re-creating service on every render
  const trailSystemService = useMemo(() => new TrailSystemService(), []) // memo to prevent re-creating service on every render

  useEffect(() => {
    if (!nested) {
      AppLayoutContext.setPageName('Trails')
    }

    if (systemId) {
      // fetch the trail system for use in the add dialog
      trailSystemService
        .get({ id: systemId, relations: [] })
        .then((system) => {
          setTrailSystem(system)
        })
        .catch((err) => {
          console.error(err)
        })
    }
  }, [])

  const relations = useMemo(() => {
    // NOTE: TrailRelations.trailforksIds is intentionally NOT requested here. The API does not
    // auto-load it on /trail search (only on /trail get), and requesting it against an API build
    // that predates the Trailforks integration causes the search to 500. The 'no TF mapping' chip
    // in the DataGrid renderCell degrades to 'no chip' when the relation is absent, which is the
    // correct behavior until the companion API PR is deployed. Add trailforksIds back to this
    // list once flowfeed-api PR #106 is merged and deployed to the preview env.
    const relations: TrailRelations[] = [TrailRelations.system, TrailRelations.ways]
    if (hasWeatherGroupPermissions) {
      relations.push(TrailRelations.weatherGroups)
    }
    if (hasConditionalStatusPermissions) {
      relations.push(TrailRelations.conditionalStatusGroups)
    }
    return relations
  }, [hasWeatherGroupPermissions, hasConditionalStatusPermissions])

  const queryClient = useQueryClient()
  const { isLoading, data: trailsResponse } = useQuery(
    [
      'TrailService.search',
      {
        ...defaultParams,
        systemId,
        page,
        pageSize,
        name,
        orderBy,
        orderDirection,
        relations,
      },
      AppStateContext.getRegion(),
    ],
    () =>
      trailService.search({
        ...defaultParams,
        systemId,
        page,
        pageSize,
        name,
        orderBy,
        orderDirection,
        relations,
      }),
    {
      onError: (err) => {
        setSearchError(typeof err === 'string' ? err : 'An error occurred while searching for trails.')
        console.error(err)
      },
    },
  )

  useEffect(() => {
    if (trailsResponse && page > 1 && (!trailsResponse.total || (page - 1) * pageSize >= trailsResponse.total)) {
      // page out of range was requested so reset to page 1
      setPage(1)
    }
  }, [trailsResponse])

  const handleRowActionsClick = (event: React.MouseEvent<HTMLButtonElement>, trail: Trail) => {
    setRowActionsAnchorEl(event.currentTarget)
    setSelectedRow(trail)
  }
  const handleRowActionsClose = () => {
    setRowActionsAnchorEl(null)
    setSelectedRow(undefined)
  }

  const columns = useMemo(() => {
    const columns: GridColDef<Trail>[] = [
      {
        field: 'actions',
        headerName: '',
        width: 60,
        sortable: false,
        renderCell: (params) => (
          <>
            <IconButton
              id={`row-actions-button-${params.row.id}`}
              color="primary"
              aria-label="row actions"
              aria-controls={selectedRow?.id === params.row.id ? 'row-actions-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={selectedRow?.id === params.row.id ? 'true' : undefined}
              onClick={(event) => handleRowActionsClick(event, params.row)}
            >
              <MoreVertIcon />
            </IconButton>
          </>
        ),
      },
      {
        field: 'id',
        headerName: 'ID',
        width: ID_COLUMN_WIDTH,
        renderCell: (params) => (
          <Link color="primary" aria-label="trail detail" href={`/trails/${params.row.id}`}>
            {params.row.id}
          </Link>
        ),
      },
      {
        field: 'name',
        headerName: 'Name',
        // takes the space left over by the other columns so the table fits its container instead of scrolling
        flex: 1,
        minWidth: 160,
        maxWidth: NAME_COLUMN_MAX_WIDTH + 80,
        renderCell: (params) => {
          // Resolve Trailforks sync per the API rule: trail value if not null, else system value, else false.
          const trailSync = params.row.trailforksSyncEnabled
          const systemSync = params.row.system?.trailforksSyncEnabled ?? false
          const resolvedSync = trailSync === null || trailSync === undefined ? systemSync : trailSync
          // Only show the "no TF mapping" chip when we ACTUALLY loaded the relation and
          // confirmed it's empty. If `trailforksIds` is undefined the relation wasn't
          // requested (search route currently omits it — see the relations useMemo note),
          // so we can't tell mapped from unmapped and MUST NOT render the chip.
          const tfIds = params.row.trailforksIds
          const tfIdsLoaded = Array.isArray(tfIds)
          const hasMapping = tfIdsLoaded && tfIds.length > 0
          const showNoMappingChip = resolvedSync && tfIdsLoaded && !hasMapping
          return (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
              <EllipsisCell title={params.row.name}>
                <Link color="primary" aria-label="trail detail" href={`/trails/${params.row.id}`}>
                  {params.row.name}
                </Link>
              </EllipsisCell>
              {showNoMappingChip && (
                <Chip
                  sx={{ flexShrink: 0 }}
                  label="no TF mapping"
                  size="small"
                  variant="outlined"
                  title="Trailforks sync is enabled for this trail but no Trailforks Trail IDs are mapped."
                />
              )}
            </Stack>
          )
        },
      },
      {
        field: 'city',
        headerName: 'City',
        width: 160,
      },
      {
        field: 'system',
        headerName: 'Trail System',
        sortable: false, // cant sort by relational fields
        flex: 0.75,
        minWidth: 160,
        maxWidth: NAME_COLUMN_MAX_WIDTH,
        renderCell: (params) => (
          <EllipsisCell title={params.row.system?.name}>
            <Link color="primary" aria-label="trail system detail" href={`/trail-systems/${params.row.system?.id}`}>
              {params.row.system?.name}
            </Link>
          </EllipsisCell>
        ),
      },
    ]
    // remove any columns specified to be removed in the excludeColumns prop
    if (excludeColumns) {
      return columns.filter((column) => !excludeColumns.includes(column.field as string))
    }
    return columns
  }, [excludeColumns])

  const handleSubmit = () => {
    setPage(1)
    setName(nameInputRef?.current?.value || '')
  }

  const handleAddClick = () => {
    setIsAdding(true)
  }

  const handleAddCancel = () => {
    setIsAdding(false)
  }

  const handleAddSubmit = () => {
    setIsAdding(false)
  }

  const handleEditClick = () => {
    setIsEditing(true)
  }

  const handleEditCancel = () => {
    setRowActionsAnchorEl(null)
    setSelectedRow(undefined)
    setIsEditing(false)
  }

  const handleEditSubmit = () => {
    setRowActionsAnchorEl(null)
    setSelectedRow(undefined)
    setIsEditing(false)
  }

  const handleDeleteClick = () => {
    // show a confirmation dialog before deleting
    setIsDeleteConfirmationOpen(true)
  }

  const handleDeleteConfirmationClose = () => {
    setIsDeleteConfirmationOpen(false)
    handleRowActionsClose()
  }

  const deleteTrail = useMutation(
    (mutationParams: { id: number }) => {
      setIsSaving(true)
      return trailService.delete(mutationParams.id)
    },
    {
      onSuccess: () => {
        SnackbarContext.show('Trail deleted successfully!', 'success', APP_UPDATE_NOTICE)
        setRowActionsAnchorEl(null)
        setSelectedRow(undefined)
        // invalidate all queries to ensure any queries that are using the modified record are updated
        queryClient.invalidateQueries()
        setIsSaving(false)
      },
      onError: (err) => {
        SnackbarContext.show(`Trail failed to delete: ${err}`, 'error')
        console.error(err)
        setIsSaving(false)
      },
    },
  )

  const handleDelete = () => {
    if (selectedRow?.id) {
      setIsDeleteConfirmationOpen(false)
      deleteTrail.mutate({ id: selectedRow.id })
    }
  }

  const openImportExport = () => {
    setIsImportExportOpen(true)
  }

  useImperativeHandle(ref, () => ({
    // expose the openImportExport function to the parent component
    openImportExport,
  }))

  const handleImportExportClose = () => {
    setIsImportExportOpen(false)
  }

  const handleImport: ImportExportDialogProps['onImport'] = (data: Partial<Trail>[], options) => {
    setIsImportExportOpen(false)
    if (!data) {
      SnackbarContext.show('Failed to read/parse file', 'error')
      console.error('Failed to read/parse file')
    } else {
      // Trailforks pusher spec: two optional columns.
      //   trailforksSyncEnabled: true / false / blank (no change)
      //   trailforksTrailIds:    pipe-separated (e.g. "638634|638647"), blank = no change,
      //                          a single '-' clears all links.
      // Convert those raw column strings into the shape the API expects.
      const preparedData = data.map((row) => {
        const anyRow = row as unknown as Record<string, unknown>
        const raw = anyRow.trailforksTrailIds
        if (typeof raw === 'string') {
          const trimmed = raw.trim()
          if (trimmed === '') {
            // blank = leave unchanged; drop the key entirely
            delete anyRow.trailforksTrailIds
          } else if (trimmed === '-') {
            anyRow.trailforksIds = []
            delete anyRow.trailforksTrailIds
          } else {
            anyRow.trailforksIds = trimmed
              .split('|')
              .map((s) => s.trim())
              .filter(Boolean)
              .map((id) => ({ trailforksTrailId: id }))
            delete anyRow.trailforksTrailIds
          }
        }
        // Normalise trailforksSyncEnabled from CSV strings.
        const syncRaw = anyRow.trailforksSyncEnabled
        if (typeof syncRaw === 'string') {
          const s = syncRaw.trim().toLowerCase()
          if (s === '') delete anyRow.trailforksSyncEnabled
          else if (s === 'true') anyRow.trailforksSyncEnabled = true
          else if (s === 'false') anyRow.trailforksSyncEnabled = false
          else if (s === 'inherit' || s === 'null') anyRow.trailforksSyncEnabled = null
        }
        return anyRow as Partial<Trail>
      })
      setIsSaving(true)
      trailService
        .import(preparedData as Partial<Trail>[], options)
        .then(() => {
          SnackbarContext.show('Trails imported successfully!', 'success', APP_UPDATE_NOTICE)

          // fetch new data
          setSearchError('')
          // invalidate all queries to ensure any queries that are using the modified record are updated
          queryClient.invalidateQueries()
          setIsSaving(false)
        })
        .catch((err) => {
          SnackbarContext.show(`Trail failed to import: ${err}`, 'error')
          console.error(err)
          setIsSaving(false)
        })
    }
  }

  const handleExport = () => {
    // do a request to the API to get the data to export with no pagination.
    //
    // Export ALSO requests trailforksIds so the exported CSV round-trips mappings
    // correctly. The grid-facing `relations` deliberately omits it (see the useMemo
    // note about the pre-#106 API 500), but export is an infrequent explicit action
    // and the API PR ships alongside this one.
    setIsSaving(true)
    setIsImportExportOpen(false)
    const exportRelations = relations.includes(TrailRelations.trailforksIds)
      ? relations
      : [...relations, TrailRelations.trailforksIds]
    trailService
      .search({
        ...defaultParams,
        systemId,
        pageSize: 0,
        name,
        orderBy,
        orderDirection,
        relations: exportRelations,
      })
      .then((results) => {
        // set a prop for system id and name and remove the system object prop
        const exportData = results.data.map((trail) => {
          const trailToExport: Record<string, unknown> = { ...trail }
          // Export weatherGroups as JSON string for CSV compatibility
          if (trail.weatherGroups && trail.weatherGroups.length > 0) {
            trailToExport.weatherGroups = JSON.stringify(
              trail.weatherGroups.map((wg) => ({
                id: wg.id,
                name: wg.name,
                priority: wg.priority,
              })),
            )
          } else {
            trailToExport.weatherGroups = ''
          }

          // Export ways as JSON string for CSV compatibility
          if (trail.ways && trail.ways.length > 0) {
            trailToExport.ways = JSON.stringify(trail.ways)
          } else {
            trailToExport.ways = ''
          }

          // Trailforks: emit pipe-separated ids in the trailforksTrailIds column
          // (the same column the importer reads). See Trailforks-Pusher-Spec.md.
          //
          // We request the trailforksIds relation on the export path (see handleExport
          // above) so `trail.trailforksIds` should always be an array here. If for some
          // reason it's undefined we OMIT the column value entirely rather than emit an
          // empty string — that avoids silently clearing every trail's mappings on
          // re-import, which is what would happen with `''`.
          if (Array.isArray(trail.trailforksIds)) {
            trailToExport.trailforksTrailIds = trail.trailforksIds.length
              ? trail.trailforksIds.map((l) => l.trailforksTrailId).join('|')
              : ''
          }
          delete trailToExport.trailforksIds

          delete trailToExport.displayWeatherGroup
          delete trailToExport.manualTrailStatuses
          delete trailToExport.features
          delete trailToExport.conditionalStatusGroups
          delete trailToExport.keywords
          // set a prop for system's id and name and remove the system object prop
          trailToExport.systemId = trail.system?.id
          trailToExport.systemName = trail.system?.name
          delete trailToExport.system

          trailToExport.updatedAt = trailToExport.updatedAt instanceof Date ? trailToExport.updatedAt.toISOString() : ''
          trailToExport.createdAt = trailToExport.createdAt instanceof Date ? trailToExport.createdAt.toISOString() : ''

          // return the object with the props in the order we want them to appear in the csv
          const propsToExport: string[] = [...trailFields, 'trailforksTrailIds']
          // add system and display weather props after weatherGroups
          propsToExport.splice(
            propsToExport.indexOf('weatherGroups') + 1,
            0,
            'systemId',
            'systemName',
            'displayWeatherGroupId',
            'displayWeatherGroupName',
          )
          return pick(trailToExport, propsToExport)
        })
        const csvData = d3.csvFormat(exportData)
        // initiate a download of the csv file
        const blob = new Blob([csvData], { type: 'text/csv' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')

        link.setAttribute('href', url)
        link.setAttribute('download', 'trails.csv')
        link.click()
        setIsSaving(false)
      })
      .catch((err) => {
        SnackbarContext.show(`Failed to fetch trails for export: ${err}`, 'error')
        console.error(err)
      })
  }

  return (
    <>
      {selectedRow && (
        <FormDialog<Trail> open={isEditing} entity={selectedRow} entityLabel="Trail" onClose={handleEditCancel}>
          <TrailForm trail={selectedRow} onEdit={handleEditSubmit} onCancel={handleEditCancel} />
        </FormDialog>
      )}
      <FormDialog<Trail> open={isAdding} entityLabel="Trail" onClose={handleAddCancel}>
        <TrailForm trailSystem={trailSystem} onAdd={handleAddSubmit} onCancel={handleAddCancel} />
      </FormDialog>
      <Dialog fullWidth maxWidth="xs" open={isDeleteConfirmationOpen} onClose={handleDeleteConfirmationClose}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent dividers>
          Are you sure you want to delete trail id {selectedRow?.id}: {selectedRow?.name}?
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={handleDeleteConfirmationClose}>
            Cancel
          </Button>
          <Button onClick={handleDelete}>Confirm</Button>
        </DialogActions>
      </Dialog>
      <ImportExportDialog
        open={isImportExportOpen}
        disableImport={disableImport || !hasWritePermissions}
        exampleImportFileUrl="/example-import-files/trails.csv"
        importNotice="Any algorithm tuning values are saved to the active tuning profile."
        importFields={[
          ...trailFields.filter(
            (field) =>
              ![
                'system',
                'weatherGroups',
                'displayWeatherGroup',
                'manualTrailStatuses',
                'conditionalStatusGroups',
                'features',
                'keywords',
                'updatedAt',
                'createdAt',
              ].includes(field),
          ),
          'systemId',
          'weatherGroupId',
          'displayWeatherGroupId',
          'ways',
          // Trailforks-Pusher-Spec.md: CSV column for pipe-separated TF ids (e.g. "638634|638647").
          'trailforksTrailIds',
        ]}
        onImport={handleImport}
        onExport={handleExport}
        onClose={handleImportExportClose}
      />
      <Paper sx={{ p: disablePadding ? 0 : 2 }}>
        {!hideBreadcrumbs && (
          <Box sx={{ mb: 2 }}>
            {' '}
            <Breadcrumbs items={[{ label: 'Trail Systems', href: '/trail-systems' }]} />
          </Box>
        )}
        {!hideHeader && (
          <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
            {trailSystem?.name ? `${trailSystem.name} ` : ''}Trails
          </Typography>
        )}
        {(isLoading || isSaving) && !searchError && <Loading sx={{ py: 20 }} />}
        {searchError && (
          <Stack direction="row" justifyContent="center" alignItems="center" sx={{ py: 20 }}>
            <Typography variant="error">{searchError}</Typography>
          </Stack>
        )}
        {!isLoading && !isSaving && !searchError && (
          <>
            {!hideActions && (
              <Box sx={{ pt: 1, mb: 1 }}>
                <Stack direction={{ zero: 'column', md: 'row' }} spacing={1}>
                  <FormControl variant="outlined" sx={{ width: '100%' }}>
                    <InputLabel>Name</InputLabel>
                    <OutlinedInput
                      id="search-name"
                      label="Name"
                      defaultValue={name || ''}
                      inputRef={nameInputRef}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSubmit()
                        }
                      }}
                      endAdornment={
                        nameInputRef?.current?.value ? (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="clear search name"
                              onClick={() => {
                                if (nameInputRef?.current?.value) {
                                  nameInputRef.current.value = ''
                                }
                                setPage(1)
                                setName('')
                              }}
                              edge="end"
                            >
                              <CloseIcon />
                            </IconButton>
                          </InputAdornment>
                        ) : undefined
                      }
                    />
                  </FormControl>

                  <Button
                    variant="outlined"
                    onClick={handleSubmit}
                    sx={(theme) => ({ [theme.breakpoints.up('md')]: { width: 150 }, maxWidth: '100%' })}
                  >
                    Search
                  </Button>
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {hasWritePermissions && (!systemId || (systemId && trailSystem)) && (
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        color="primary"
                        aria-label="add new"
                        onClick={handleAddClick}
                      >
                        Add
                      </Button>
                    )}
                    <Button
                      variant="outlined"
                      startIcon={<ImportExportIcon />}
                      color="primary"
                      aria-label="show import export"
                      onClick={() => {
                        openImportExport()
                      }}
                    >
                      {!disableImport ? 'Import / ' : ''}Export
                    </Button>
                  </Stack>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => {
                      if (nameInputRef?.current?.value) {
                        nameInputRef.current.value = ''
                      }
                      setPage(1)
                      setName('')
                    }}
                  >
                    Clear
                  </Button>
                </Stack>
              </Box>
            )}

            {trailsResponse?.data && trailsResponse.data.length > 0 ? (
              <>
                <Menu
                  id="row-actions-menu"
                  anchorEl={rowActionsAnchorEl}
                  open={isActionsMenuOpen}
                  onClose={handleRowActionsClose}
                  MenuListProps={{
                    'aria-labelledby': `row-actions-button${selectedRow?.id}`,
                  }}
                >
                  <MenuItem href={`/trails/${selectedRow?.id}`} component={Link}>
                    View
                  </MenuItem>
                  {!hideActions && hasWritePermissions && <MenuItem onClick={handleEditClick}>Edit</MenuItem>}
                  {!hideActions && hasWritePermissions && <MenuItem onClick={handleDeleteClick}>Delete</MenuItem>}
                  {Array.isArray(extraRowActions) && extraRowActions.length > 0 && <Divider sx={{ my: 0.5 }} />}
                  {Array.isArray(extraRowActions) &&
                    extraRowActions.length > 0 &&
                    extraRowActions.map((action, index) => (
                      <MenuItem key={index} onClick={() => action.onClick(selectedRow)}>
                        {action.label}
                      </MenuItem>
                    ))}
                </Menu>
                <DataTable
                  columns={columns}
                  data={trailsResponse.data}
                  total={trailsResponse.total}
                  paginationMode="server"
                  page={page}
                  pageSize={pageSize}
                  pageSizeOptions={[12, 24, 48, 96]}
                  onPageChange={(newPage) => {
                    setPage(newPage)
                  }}
                  onPageSizeChange={(newPageSize) => {
                    setPageSize(newPageSize)
                  }}
                  orderBy={orderBy}
                  orderDirection={orderDirection}
                  onOrderChange={(newOrder) => {
                    setOrderBy(newOrder.field ? (newOrder.field as keyof Trail) : undefined)
                    setOrderDirection(newOrder.sort)
                  }}
                />
              </>
            ) : (
              <Box sx={{ p: 2 }}>
                <Typography>No results found</Typography>
              </Box>
            )}
          </>
        )}
      </Paper>
    </>
  )
})
export default TrailSearch
